import React from 'react';
import { ArrowRight, FolderKanban } from 'lucide-react';

import { useAppTheme } from '../hooks/useAppTheme';
import ProjectGateHeader from './ProjectGateHeader';
import ProjectGateVisualPanel from './ProjectGateVisualPanel';
import { Badge } from './ui';
import { cn } from '../utils/cn';
import { getPublicImageSrc } from '../utils/publicAssets';
import '../styles/project-selection.css';

export type ProjectSelectionMeta = {
  label: string;
  value: string;
};

export type ProjectSelectionItem = {
  id: number | string;
  name: string;
  companyName?: string | null;
  status?: string | null;
  meta?: ProjectSelectionMeta[];
  selected?: boolean;
};

export type ProjectSelectionScreenProps = {
  title?: string;
  eyebrow?: string;
  subtitle: string;
  projects: ProjectSelectionItem[];
  onSelect: (id: number | string) => void;
  emptyTitle?: string;
  emptyHint?: string;
  profileSlot?: React.ReactNode;
  /** Render picker only (no gate chrome) — for shell pages like SME tracking. */
  embedded?: boolean;
  /** Override or hide the grid heading under the welcome card. */
  pickerHeading?: string | null;
  className?: string;
};

const statusBadgeVariant = (
  status?: string | null
): 'default' | 'primary' | 'success' | 'warning' | 'danger' => {
  const value = (status || '').toLowerCase();
  if (value.includes('complete')) return 'success';
  if (value.includes('progress')) return 'primary';
  if (value.includes('error')) return 'danger';
  if (value.includes('not started')) return 'default';
  return 'default';
};

export const ProjectSelectionPicker: React.FC<{
  projects: ProjectSelectionItem[];
  onSelect: (id: number | string) => void;
  emptyTitle?: string;
  emptyHint?: string;
  heading?: string | null;
}> = ({
  projects,
  onSelect,
  emptyTitle = 'No projects available',
  emptyHint = 'Projects assigned to your account will appear here.',
  heading = 'Select a project',
}) => {
  if (projects.length === 0) {
    return (
      <div className="ps-picker__empty" role="status">
        <p className="ps-picker__empty-title">{emptyTitle}</p>
        <p className="ps-picker__empty-hint">{emptyHint}</p>
      </div>
    );
  }

  return (
    <div className="ps-picker">
      {heading ? <h2 className="ps-picker__heading">{heading}</h2> : null}
      <div className="ps-picker__grid">
        {projects.map((project) => {
          const metaRows: ProjectSelectionMeta[] = [
            ...(project.companyName
              ? [{ label: 'Company', value: project.companyName }]
              : []),
            ...(project.meta || []),
          ];

          return (
            <button
              key={String(project.id)}
              type="button"
              className={cn('ps-picker__card', project.selected && 'is-selected')}
              onClick={() => onSelect(project.id)}
            >
              <div className="ps-picker__card-top">
                <div>
                  <p className="ps-picker__label">Project</p>
                  <h3 className="ps-picker__name">{project.name}</h3>
                </div>
                {project.status ? (
                  <Badge variant={statusBadgeVariant(project.status)}>
                    {project.status}
                  </Badge>
                ) : null}
              </div>

              {metaRows.length > 0 ? (
                <ul className="ps-picker__meta">
                  {metaRows.map((row) => (
                    <li key={`${row.label}-${row.value}`}>
                      <strong>{row.label}:</strong>
                      <span>{row.value}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <span className="ps-picker__cta">
                Open project
                <ArrowRight size={14} aria-hidden />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Post-login project gate for Client / SuperUser, or embedded picker for SME.
 */
const ProjectSelectionScreen: React.FC<ProjectSelectionScreenProps> = ({
  title = 'Your projects',
  eyebrow = 'Get started',
  subtitle,
  projects,
  onSelect,
  emptyTitle,
  emptyHint,
  profileSlot,
  embedded = false,
  pickerHeading,
  className,
}) => {
  useAppTheme();

  const projectCount = projects.length;
  const projectCountLabel =
    projectCount === 1 ? '1 project' : `${projectCount} projects`;

  const basename = process.env.PUBLIC_URL || '';

  const resolvedPickerHeading =
    pickerHeading !== undefined
      ? pickerHeading
      : embedded
        ? 'Select a project'
        : 'Choose a project';

  const welcomeCard = (
    <div className="app-welcome-card ps-gate__welcome">
      <div className="app-welcome-card-glow" aria-hidden />
      <div className="ps-gate__welcome-inner">
        <p className="ps-gate__eyebrow">{eyebrow}</p>
        <h1 className="ps-gate__title app-heading-accent">{title}</h1>
        <p className="ps-gate__subtitle">{subtitle}</p>
        {projectCount > 0 ? (
          <p className="ps-gate__chips">
            <span className="app-welcome-card-chip">
              <FolderKanban size={14} aria-hidden />
              {projectCountLabel}
            </span>
          </p>
        ) : null}
      </div>
    </div>
  );

  const picker = (
    <ProjectSelectionPicker
      projects={projects}
      onSelect={onSelect}
      emptyTitle={emptyTitle}
      emptyHint={emptyHint}
      heading={resolvedPickerHeading}
    />
  );

  if (embedded) {
    return (
      <div className={cn('ps-embedded', className)}>
        {welcomeCard}
        {picker}
      </div>
    );
  }

  return (
    <div
      className={cn('ps-gate app-page-background', className)}
      style={
        {
          '--ps-gate-watermark': `url(${getPublicImageSrc('RadarGrid.svg', basename)})`,
        } as React.CSSProperties
      }
    >
      <ProjectGateHeader actions={profileSlot} />

      <main className="ps-gate__main">
        <div className="ps-gate__layout">
          <ProjectGateVisualPanel />

          <div className="ps-gate__content">
            <div className="ps-gate__intro">{welcomeCard}</div>
            <section className="ps-gate__picker" aria-label="Project list">
              {picker}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProjectSelectionScreen;

/** Shared icon for empty/loading states that want a visual anchor. */
export const ProjectSelectionIcon = FolderKanban;
